// ✅ src/components/UserEdit.js
import React, { useEffect, useMemo, useState, useContext } from "react";
import "../styles/UserForm.css";
import { AuthContext } from "../context/AuthContext";
import { updateUser } from "../services/userService";
import { getRolesByTenant } from "../services/roleService";
import { getLocationsByTenant } from "../services/locationService";
import { getDepartmentsByLocation } from "../services/departmentService";
import { getTenants } from "../services/tenantService";

function UserEdit({ userData, onClose, onSuccess }) {
  const { user, activeScope } = useContext(AuthContext);
  const isSuperAdmin = (user?.role_code || "").toUpperCase() === "SUPER_ADMIN";

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [tenants, setTenants] = useState([]);
  const [roles, setRoles] = useState([]);
  const [locations, setLocations] = useState([]);
  const [departments, setDepartments] = useState([]);

  const [form, setForm] = useState({
    email: "",
    tenant_id: "",
    role_id: "",
    location_ids: [],
    department_ids: [],
    is_employee: false,
  });

  const [validationErrors, setValidationErrors] = useState({});

  const defaultTenant = useMemo(() => {
    return userData?.tenant_id || activeScope?.tenant_id || user?.tenant_id || "";
  }, [userData, activeScope?.tenant_id, user?.tenant_id]);

  // ✅ Load initial form data
  useEffect(() => {
    if (userData) {
      setForm({
        email: userData.email || "",
        tenant_id: userData.tenant_id || defaultTenant,
        role_id: userData.role_id ? String(userData.role_id) : "",
        location_ids: userData.location_id ? [String(userData.location_id)] : [],
        department_ids: userData.department_id ? [String(userData.department_id)] : [],
        is_employee: !!userData.is_employee,
      });
    }
  }, [userData, defaultTenant]);

  const updateForm = (key, value) => {
    setValidationErrors((e) => ({ ...e, [key]: false }));
    setForm((f) => ({ ...f, [key]: value }));
  };

  // ✅ Load tenants IF SuperAdmin
  useEffect(() => {
    let cancelled = false;
    if (!isSuperAdmin) return;

    const loadTenants = async () => {
      try {
        setLoading(true);
        const res = await getTenants();
        if (!cancelled && res?.success) setTenants(res.data || []);
      } finally {
        setLoading(false);
      }
    };

    loadTenants();
    return () => (cancelled = true);
  }, [isSuperAdmin]);

  // ✅ Load roles + locations when tenant changes
  useEffect(() => {
    let cancelled = false;
    if (!form.tenant_id) return;

    const loadData = async () => {
      try {
        setLoading(true);
        const roleRes = await getRolesByTenant(form.tenant_id);
        if (!cancelled && roleRes?.success) setRoles(roleRes.data || []);

        const locRes = await getLocationsByTenant(form.tenant_id);
        if (!cancelled && locRes?.success) setLocations(locRes.data || []);
      } finally {
        setLoading(false);
      }
    };

    loadData();
    return () => (cancelled = true);
  }, [form.tenant_id]);

  // ✅ Load departments when location selection changes
  useEffect(() => {
    let cancelled = false;

    const loadDeps = async () => {
      if (!form.tenant_id || !form.location_ids.length) {
        setDepartments([]);
        updateForm("department_ids", []);
        return;
      }

      const all = [];
      for (const locID of form.location_ids) {
        const d = await getDepartmentsByLocation(form.tenant_id, locID);
        if (d?.success) all.push(...d.data);
      }
      const unique = [...new Map(all.map((d) => [d.id, d])).values()];
      if (!cancelled) setDepartments(unique);
    };

    loadDeps();
    return () => (cancelled = true);
  }, [form.location_ids, form.tenant_id]);

  // ✅ Validation rules
  const validate = () => {
    const errors = {};
    if (!form.email.trim()) errors.email = true;
    if (!form.tenant_id) errors.tenant_id = true;
    if (!form.role_id) errors.role_id = true;
    if (!form.location_ids.length) errors.location_ids = true;

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ✅ Submit changes
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!validate()) {
      setError("Please fill all required fields");
      return;
    }

    const payload = {
      email: form.email.trim(),
      tenant_id: Number(form.tenant_id),
      role_id: Number(form.role_id),
      location_ids: form.location_ids.map(Number),
      department_ids: form.department_ids.map(Number),
      is_employee: !!form.is_employee,
    };

    try {
      setSaving(true);
      const res = await updateUser(userData.id, payload);

      // ✅ Success Response Check FIXED
      if (res?.data?.success || res?.success) {
        onSuccess?.();
        onClose?.();
        return;
      }

      setError(res?.data?.message || res?.message || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const onTenantChange = (value) => {
    updateForm("tenant_id", value);
    setRoles([]);
    setLocations([]);
    setDepartments([]);
    updateForm("role_id", "");
    updateForm("location_ids", []);
    updateForm("department_ids", []);
  };

  return (
    <div className="uf-modal" onClick={onClose}>
      <div className="uf-card" onClick={(e) => e.stopPropagation()}>
        
        {/* ✅ Gradient Header */}
        <div className="uf-header">
          <h5>Edit User</h5>
          <button onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="uf-body">

            {error && <div className="alert alert-danger">{error}</div>}

            <div className="uf-grid">

              {/* Username (read-only) */}
              <div>
                <label className="form-label">Username</label>
                <input
                  className="form-control"
                  readOnly
                  value={userData.username}
                />
              </div>

              {/* Email */}
              <div>
                <label className="form-label">Email *</label>
                <input
                  type="email"
                  className={`form-control ${validationErrors.email ? "is-invalid" : ""}`}
                  value={form.email}
                  onChange={(e) => updateForm("email", e.target.value)}
                />
              </div>

              {/* Tenant */}
              <div>
                <label className="form-label">Tenant *</label>
                {isSuperAdmin ? (
                  <select
                    className={`form-select ${validationErrors.tenant_id ? "is-invalid" : ""}`}
                    value={String(form.tenant_id || "")}
                    onChange={(e) => onTenantChange(e.target.value)}
                  >
                    <option value="">Select Tenant</option>
                    {tenants.map((t) => (
                      <option key={t.id} value={String(t.id)}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    className="form-control"
                    readOnly
                    value={userData.tenant_name || `Tenant #${form.tenant_id}`}
                  />
                )}
              </div>

              {/* Role */}
              <div>
                <label className="form-label">Role *</label>
                <select
                  className={`form-select ${validationErrors.role_id ? "is-invalid" : ""}`}
                  value={String(form.role_id || "")}
                  onChange={(e) => updateForm("role_id", e.target.value)}
                >
                  <option value="">Select Role</option>
                  {roles.map((r) => (
                    <option key={r.id} value={String(r.id)}>
                      {r.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Locations */}
              <div>
                <label className="form-label">Locations *</label>
                <select
                  multiple
                  className={`form-select uf-select ${validationErrors.location_ids ? "is-invalid" : ""}`}
                  value={form.location_ids}
                  onChange={(e) =>
                    updateForm(
                      "location_ids",
                      [...e.target.selectedOptions].map((o) => o.value)
                    )
                  }
                >
                  {locations.map((l) => (
                    <option key={l.id} value={String(l.id)}>
                      {l.name}
                    </option>
                  ))}
                </select>
                <small>Select multiple using Ctrl/Cmd</small>
              </div>

              {/* Departments */}
              <div>
                <label className="form-label">Departments</label>
                <select
                  multiple
                  className="form-select uf-select"
                  value={form.department_ids}
                  onChange={(e) =>
                    updateForm(
                      "department_ids",
                      [...e.target.selectedOptions].map((o) => o.value)
                    )
                  }
                >
                  {departments.map((d) => (
                    <option key={d.id} value={String(d.id)}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Is Employee */}
              <div className="form-check form-switch mt-4">
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={form.is_employee}
                  onChange={(e) =>
                    updateForm("is_employee", e.target.checked)
                  }
                />
                <label className="form-check-label">Also an Employee?</label>
              </div>
            </div>
          </div>

          {/* ✅ Footer */}
          <div className="uf-actions">
            <button type="button" className="btn btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button className="btn btn-primary" type="submit" disabled={saving}>
              {saving ? "Saving…" : "Save Changes"}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
}

export default UserEdit;
